import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Palette, BarChart3, Brush, Target, Video,
  ArrowRight, Loader2, Briefcase, BookOpen, Headphones, Zap, ShoppingBag, Plus, MessageSquare
} from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
import { servicesService } from '../../services/ServicesService';
import { stripeService } from '../../services/StripeService';
import { ADDON_CATALOG, getAddon } from '../../constants/addonCatalog';
import EbookViewer from './EbookViewer';
import EmbeddedCheckoutModal from '../../components/checkout/EmbeddedCheckoutModal';

const PARTNER_CARDS = [
  {
    type: 'identidade_visual',
    icon: Palette,
    title: 'Identidade Visual',
    description: 'Criação de logotipo, paleta de cores, tipografia e manual de marca completo para o seu negócio.',
    gradient: 'from-pink-500 to-rose-500',
    bgLight: 'from-pink-50 to-rose-50',
  },
  {
    type: 'gestao_trafego',
    icon: BarChart3,
    title: 'Gestão de Tráfego Pago',
    description: 'Campanhas de anúncios no Instagram, Facebook e Google Ads com estratégia e otimização contínua.',
    gradient: 'from-blue-500 to-indigo-500',
    bgLight: 'from-blue-50 to-indigo-50',
  },
  {
    type: 'branding',
    icon: Brush,
    title: 'Branding',
    description: 'Posicionamento de marca, tom de voz, storytelling e estratégia de comunicação visual.',
    gradient: 'from-purple-500 to-violet-500',
    bgLight: 'from-purple-50 to-violet-50',
  },
  {
    type: 'videomaker',
    icon: Video,
    title: 'Videomaker',
    description: 'Produção audiovisual sob medida para seu negócio: reels, vídeos institucionais, edições e cortes profissionais.',
    gradient: 'from-amber-500 to-orange-500',
    bgLight: 'from-amber-50 to-orange-50',
  },
];

const ServicesPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [purchasedAddons, setPurchasedAddons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCount, setActiveCount] = useState(0);
  const [openEbookId, setOpenEbookId] = useState(null);
  const [addonCheckoutSecret, setAddonCheckoutSecret] = useState(null);
  const [buyingAddonId, setBuyingAddonId] = useState(null);
  const [buyError, setBuyError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketsRes, countRes, subRes] = await Promise.all([
          servicesService.listTickets(),
          servicesService.getActiveCount(),
          stripeService.getSubscriptionStatus().catch(() => null),
        ]);
        setTickets(ticketsRes.data || []);
        setActiveCount(countRes.data?.count || 0);
        setPurchasedAddons(subRes?.data?.purchased_addons || []);
      } catch (err) {
        console.error('Erro ao buscar dados de servicos:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const canCreateTicket = activeCount < servicesService.helpers.MAX_ACTIVE_TICKETS;

  // De-duplicar addons comprados (caso usuario tenha comprado o mesmo addon mais de uma vez,
  // exibir um unico card — o entitlement e por addon_id, nao por compra).
  const uniqueAddons = useMemo(() => {
    const seen = new Set();
    return (purchasedAddons || []).filter((p) => {
      if (!p?.addon_id || seen.has(p.addon_id)) return false;
      seen.add(p.addon_id);
      return true;
    });
  }, [purchasedAddons]);

  // Mapa ticket consultoria por addon — o webhook cria um ticket com service_type=consultoria_60min
  // e (idealmente) stripe_session_id batendo. Casamos por session_id quando disponivel.
  const consultoriaTicketBySession = useMemo(() => {
    const map = {};
    for (const t of tickets || []) {
      if (t.service_type === 'consultoria_60min' && t.stripe_session_id) {
        map[t.stripe_session_id] = t;
      }
    }
    return map;
  }, [tickets]);

  const handleBuyAddon = async (addonId) => {
    setBuyingAddonId(addonId);
    setBuyError('');
    try {
      const res = await stripeService.createAddonCheckoutSession([addonId]);
      const secret = res?.data?.client_secret;
      if (!secret) throw new Error('Sessao de checkout invalida.');
      setAddonCheckoutSecret(secret);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Falha ao iniciar checkout.';
      setBuyError(typeof detail === 'string' ? detail : 'Falha ao iniciar checkout.');
    } finally {
      setBuyingAddonId(null);
    }
  };

  const handleAddonClick = (addon) => {
    if (addon.type === 'ebook' || addon.id === 'ebook_instagram_vende') {
      setOpenEbookId(addon.id);
      return;
    }
    if (addon.type === 'consultoria' || addon.id === 'consultoria_60min') {
      // Procurar ticket associado; se nao houver, leva pra lista de tickets do usuario.
      const sessionId = (purchasedAddons.find((p) => p.addon_id === addon.id) || {}).stripe_session_id;
      const ticket = sessionId ? consultoriaTicketBySession[sessionId] : null;
      if (ticket) {
        navigate(`/services/tickets/${ticket.id}`);
      } else {
        // Fallback: encontrar qualquer ticket de consultoria do usuario
        const anyConsultoria = (tickets || []).find((t) => t.service_type === 'consultoria_60min');
        if (anyConsultoria) navigate(`/services/tickets/${anyConsultoria.id}`);
        else navigate('/services');
      }
      return;
    }
    // Tipo desconhecido — apenas log
    console.warn('Addon sem handler:', addon.id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 shadow-sm">
            <div className="flex items-center justify-between h-20">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Serviços</p>
                  <h1 className="text-lg font-bold text-gray-900">Potencialize seu negócio</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 space-y-10">

        {/* ==================== NOSSOS SERVICOS (comprados + disponiveis para compra) ==================== */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Nossos serviços
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          ) : (
            <>
              {buyError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-2 mb-3">
                  {buyError}
                </div>
              )}

              {/* Loop unico na ordem do ADDON_CATALOG (a ordem do catalogo eh fonte da verdade) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ADDON_CATALOG.map((addon) => {
                  const Icon = addon.icon || (addon.type === 'consultoria' ? Headphones : BookOpen);
                  const isConsultoria = addon.type === 'consultoria';
                  // E-book: 'comprado' eh entitlement unico. Consultoria: sempre permite recompra.
                  const isOwned = (purchasedAddons || []).some((p) => p.addon_id === addon.id);
                  const showBuyButton = isConsultoria || !isOwned;
                  const isBuying = buyingAddonId === addon.id;

                  return (
                    <div
                      key={addon.id}
                      className={`bg-white rounded-2xl p-5 transition-all flex flex-col ${
                        isOwned
                          ? 'border border-gray-200 hover:shadow-lg'
                          : 'border-2 border-dashed border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                        isConsultoria ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-bold text-gray-900">{addon.name}</h4>
                        {!isOwned && (
                          <span className={`text-sm font-bold whitespace-nowrap ${isConsultoria ? 'text-blue-600' : 'text-amber-600'}`}>
                            {addon.priceFormatted}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2 flex-1">
                        {addon.tagline || addon.description}
                      </p>

                      {isOwned && !isConsultoria && (
                        // E-book comprado: card inteiro abre o PDF
                        <button
                          type="button"
                          onClick={() => handleAddonClick(addon)}
                          className="w-full mt-auto inline-flex items-center justify-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700"
                        >
                          <Zap className="w-4 h-4" /> Ler agora
                        </button>
                      )}

                      {isOwned && isConsultoria && (
                        // Consultoria comprada: 'Acessar chat' principal + recompra discreto
                        <>
                          <button
                            type="button"
                            onClick={() => handleAddonClick(addon)}
                            className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl transition-all mb-2"
                          >
                            <Zap className="w-4 h-4" /> Acessar chat
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBuyAddon(addon.id)}
                            disabled={isBuying}
                            className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                          >
                            {isBuying ? 'Abrindo checkout...' : `Comprar nova sessão (${addon.priceFormatted})`}
                          </button>
                        </>
                      )}

                      {showBuyButton && !isOwned && (
                        // Nao comprado: botao 'Comprar R$ X'
                        <>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                            <Zap className="w-3.5 h-3.5 text-green-600" />
                            <span>Acesso imediato após o pagamento</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleBuyAddon(addon.id)}
                            disabled={isBuying}
                            className={`w-full py-2 text-sm font-medium rounded-xl transition-all disabled:opacity-50 ${
                              isConsultoria
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white'
                            }`}
                          >
                            {isBuying ? (
                              <span className="inline-flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Abrindo checkout...
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center gap-1.5">
                                <Plus className="w-4 h-4" /> Comprar {addon.priceFormatted}
                              </span>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {ADDON_CATALOG.length === 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center">
                    <ShoppingBag className="w-7 h-7 text-amber-400" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Nenhum serviço disponível</h3>
                </div>
              )}
            </>
          )}
        </section>

        {/* ==================== NOSSOS PARCEIROS ==================== */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Parceiros
            </h2>
            <span className="text-xs text-gray-400">
              {activeCount}/{servicesService.helpers.MAX_ACTIVE_TICKETS} serviços ativos
            </span>
          </div>
          {!canCreateTicket && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
              Você atingiu o limite de {servicesService.helpers.MAX_ACTIVE_TICKETS} serviços ativos. Aguarde a conclusão ou encerramento de um serviço.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PARTNER_CARDS.map((service) => {
              const Icon = service.icon;
              // Procura ticket ativo desse parceiro pra mostrar 'Ver conversa' em vez de 'Falar agora'
              const activeTicket = (tickets || []).find(
                (t) => t.service_type === service.type && !servicesService.helpers.isTicketClosed(t.status)
              );
              const hasActive = !!activeTicket;
              const cardClickable = hasActive || canCreateTicket;
              const onCardClick = hasActive
                ? () => navigate(`/services/tickets/${activeTicket.id}`)
                : () => canCreateTicket && navigate(`/services/request?type=${service.type}`);
              return (
                <div
                  key={service.type}
                  className={`bg-white rounded-2xl border border-gray-200 p-6 transition-all group flex flex-col ${
                    cardClickable ? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer' : 'opacity-60 cursor-not-allowed'
                  }`}
                  onClick={onCardClick}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.bgLight} flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6`} style={{ color: service.gradient.includes('pink') ? '#ec4899' : service.gradient.includes('blue') ? '#3b82f6' : service.gradient.includes('purple') ? '#8b5cf6' : '#f59e0b' }} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-3 flex-1">{service.description}</p>
                  {hasActive ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${servicesService.helpers.getStatusColor(activeTicket.status)}`}>
                        {servicesService.helpers.getStatusLabel(activeTicket.status)}
                      </span>
                      <div className="flex items-center text-sm font-semibold text-amber-600 group-hover:text-amber-700">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Ver conversa
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center text-sm font-semibold text-amber-600 group-hover:text-amber-700">
                      Falar agora
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {openEbookId && (
        <EbookViewer addonId={openEbookId} onClose={() => setOpenEbookId(null)} />
      )}

      {addonCheckoutSecret && (
        <EmbeddedCheckoutModal
          clientSecret={addonCheckoutSecret}
          onClose={() => {
            setAddonCheckoutSecret(null);
            // Recarregar dados pra refletir a nova compra (purchased_addons + tickets)
            (async () => {
              try {
                const [ticketsRes, subRes] = await Promise.all([
                  servicesService.listTickets(),
                  stripeService.getSubscriptionStatus().catch(() => null),
                ]);
                setTickets(ticketsRes.data || []);
                setPurchasedAddons(subRes?.data?.purchased_addons || []);
              } catch (e) {
                console.error('refresh apos compra falhou:', e);
              }
            })();
          }}
        />
      )}
    </div>
  );
};

export default ServicesPage;
